import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowLeft, ShieldCheck, ArrowUpRight, Lock, AlertTriangle, Mail, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function BrandMark({ size = 28 }) {
  return (
    <span
      className="grid place-items-center rounded-[8px] text-white font-black brand-gradient tracking-tighter"
      style={{ width: size, height: size, fontSize: size * 0.45, boxShadow: "0 6px 18px rgba(91,140,255,0.35)" }}>
      L
    </span>
  );
}

const ERROR_COPY = {
  not_invited: {
    title: "You haven't been invited yet.",
    body: "Please ask your workspace admin to send you an invite. Once they do, click the link in your invitation email and try again.",
  },
  signin_failed: {
    title: "Sign-in failed.",
    body: "We couldn't complete the sign-in. Try again in a moment, or contact your admin if this keeps happening.",
  },
};

export default function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const errorCode = params.get("error");
  const err = ERROR_COPY[errorCode];
  const [magicOpen, setMagicOpen] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicBusy, setMagicBusy] = useState(false);
  const [magicSent, setMagicSent] = useState(false);
  // Silent "remember this device" attempt on mount - the SPA fires ONCE.
  // 200 → auto-redirect to the user's landing page; 401 → render normal login.
  const [trustConsuming, setTrustConsuming] = useState(true);

  useEffect(() => {
    // Skip silent consume when the user landed on /login with an explicit
    // error (e.g. not_invited) - they need to see the message.
    if (errorCode) { setTrustConsuming(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/auth/device-trust/consume", {});
        if (cancelled) return;
        // Fresh session cookie is on the response - navigate immediately
        // using the role in the consume response (do NOT await checkAuth;
        // the httpOnly cookie may not be visible to the follow-up /auth/me
        // request until the next tick, causing a false-401 race that
        // strands the user on /login).
        const role = data?.role;
        const dest = role === "super_admin" ? "/super-admin" : "/app/dashboard";
        navigate(dest, { replace: true });
        // Fire-and-forget hydration of the auth context so the destination
        // has fresh state; failures here are non-fatal - AppShell will
        // re-attempt on mount.
        checkAuth().catch(() => {});
      } catch {
        // 401 is the expected happy path when no trust cookie exists.
        if (!cancelled) setTrustConsuming(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCode]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Iter 131.6.11 - add ?prompt=select_account so Google shows its
    // account picker even when the browser has a default session. Belt-
    // and-suspenders on top of the logout-side device_trust revoke: if
    // a user manages to reach the login page while still signed into
    // Google, they can still pick a different account.
    const redirectUrl = window.location.origin + "/app/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&prompt=select_account`;
  };

  const requestMagicLink = async () => {
    const email = magicEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    setMagicBusy(true);
    try {
      await api.post("/auth/magic-link/request", { email, origin_url: window.location.origin });
      // Never reveal whether the email exists - always show success
      setMagicSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send link");
    } finally { setMagicBusy(false); }
  };

  // While the silent consume is in-flight, render a small spinner instead
  // of flashing the login form. Bounded to ~1s in practice (single fetch).
  if (trustConsuming) {
    return (
      <div className="min-h-screen bg-brand-bg text-brand-ink relative overflow-hidden" data-testid="login-trust-consuming">
        <div className="absolute inset-0 brand-hero-glow pointer-events-none" />
        <div className="relative flex items-center justify-center min-h-screen">
          <Loader2 className="w-6 h-6 text-brand-accent animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink relative overflow-hidden" data-testid="login-page">
      <div className="absolute inset-0 brand-hero-glow pointer-events-none" />
      <div className="absolute inset-0 brand-grid pointer-events-none opacity-50" />

      {/* top bar */}
      <div className="relative flex items-center justify-between px-5 lg:px-10 py-5">
        <Link to="/" className="flex items-center gap-2.5" data-testid="login-brand-home">
          <BrandMark />
          <span className="text-sm font-semibold tracking-tight font-editorial">LyraOS</span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-brand-mut hover:text-brand-ink" data-testid="back-home">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Link>
      </div>

      <div className="relative flex items-center justify-center px-5 py-10 lg:py-24">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 lg:mb-10">
            <div className="inline-flex items-center gap-2 border border-brand rounded-full pl-1.5 pr-3 py-1 mb-6 text-xs">
              <span className="brand-gradient text-white text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full">Portal</span>
              <span className="text-brand-mut">Staff sign-in</span>
            </div>
            <h1 className="font-editorial text-4xl sm:text-5xl text-brand-ink tracking-tight leading-[1.05]">
              Welcome <span className="brand-gradient-text">back.</span>
            </h1>
            <p className="mt-4 text-brand-mut">Sign in with your organisation Google account.</p>
          </div>

          <div className="brand-card p-6 shadow-[0_20px_60px_-20px_rgba(91,140,255,0.28)]">
            {err && (
              <div className="mb-5 p-3 rounded-lg bg-red-500/10 border border-red-500/30 flex gap-2.5" data-testid="login-error-banner">
                <AlertTriangle className="w-4 h-4 text-red-300 shrink-0 mt-0.5" />
                <div className="text-left">
                  <div className="text-sm font-semibold text-red-200">{err.title}</div>
                  <div className="text-xs text-red-200/80 mt-1">{err.body}</div>
                </div>
              </div>
            )}
            <button
              onClick={handleGoogleLogin}
              data-testid="google-login-btn"
              className="group w-full h-12 brand-btn-primary rounded-full font-semibold text-sm inline-flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.6 32.9 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.3 6 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.7 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.3 6 29.4 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.3 34.9 26.8 36 24 36c-5.1 0-9.5-3-11.3-7.4l-6.5 5C9.5 39.7 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.9 2.4-2.4 4.4-4.5 5.8l6.3 5.3C41.7 35.2 44 30 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
              Continue with Google
              <ArrowUpRight className="w-3.5 h-3.5 opacity-90 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>

            {/* Passwordless fallback - appears below Google button */}
            {!magicOpen ? (
              <button
                onClick={() => { setMagicOpen(true); setMagicSent(false); }}
                data-testid="magic-link-open-btn"
                className="mt-3 w-full h-10 rounded-full border border-brand text-brand-ink text-xs inline-flex items-center justify-center gap-2 hover:bg-white/5 transition-colors"
              >
                <Mail className="w-3.5 h-3.5" /> Sign in with email link →
              </button>
            ) : magicSent ? (
              <div className="mt-4 p-4 rounded-lg bg-brand-good/10 border border-brand-good/30 text-left" data-testid="magic-link-sent">
                <div className="text-sm font-semibold text-brand-good mb-1">Check your inbox</div>
                <div className="text-xs text-brand-mut leading-relaxed">
                  If <span className="text-brand-ink font-mono">{magicEmail}</span> is a registered LyraOS user, a one-time sign-in link is on its way (valid 15 min). Didn't get it? Check spam, or try again in a moment.
                </div>
                <button
                  onClick={() => { setMagicOpen(false); setMagicSent(false); setMagicEmail(""); }}
                  className="mt-3 text-xs text-brand-accent hover:underline"
                  data-testid="magic-link-dismiss"
                >
                  ← Back to sign in
                </button>
              </div>
            ) : (
              <div className="mt-4 p-4 rounded-lg border border-brand" data-testid="magic-link-form">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-brand-mut">We'll email a one-time link (valid 15 min).</div>
                  <button onClick={() => setMagicOpen(false)} className="text-brand-mut hover:text-brand-ink" aria-label="Close magic link form">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <input
                  type="email"
                  value={magicEmail}
                  onChange={(e) => setMagicEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") requestMagicLink(); }}
                  placeholder="you@yourfirm.com"
                  data-testid="magic-link-email-input"
                  autoFocus
                  className="w-full px-3 py-2 rounded-md bg-white/5 border border-brand text-brand-ink text-sm placeholder:text-brand-dim focus:outline-none focus:ring-1 focus:ring-brand-accent"
                />
                <button
                  onClick={requestMagicLink}
                  disabled={magicBusy}
                  data-testid="magic-link-submit"
                  className="mt-2 w-full h-10 rounded-full brand-btn-primary text-white text-xs font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {magicBusy ? "Sending…" : "Email me a sign-in link"}
                </button>
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-brand grid grid-cols-3 gap-3 text-center">
              <div>
                <ShieldCheck className="w-4 h-4 text-brand-accent mx-auto mb-1.5" />
                <div className="text-[10px] font-mono uppercase tracking-wider text-brand-dim">MFA</div>
              </div>
              <div>
                <div className="w-4 h-4 mx-auto mb-1.5 flex items-center justify-center">
                  <div className="w-1.5 h-1.5 rounded-full bg-brand-good animate-pulse-dot" />
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-brand-dim">Audit</div>
              </div>
              <div>
                <Lock className="w-4 h-4 text-brand-gold mx-auto mb-1.5" />
                <div className="text-[10px] font-mono uppercase tracking-wider text-brand-dim">TLS 1.2+</div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center text-xs text-brand-dim">
            By continuing you agree to LyraOS terms and privacy policy. Access is limited to authorised users of your workspace.
          </div>
          <div className="mt-4 text-center text-xs text-brand-mut">
            New to LyraOS? <Link to="/signup" className="text-brand-ink underline underline-offset-4 hover:text-brand-accent" data-testid="login-signup-pointer">Start a free trial →</Link>
          </div>
          <div className="mt-2 text-center text-xs text-brand-mut">
            Are you a client? <Link to="/portal/login" className="text-brand-ink underline underline-offset-4 hover:text-brand-accent" data-testid="staff-login-client-pointer">Sign in to the Client Portal →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
